import React, { useState, useEffect } from 'react';
import Modal from "react-modal";
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { getRouteLetterByMissionId, softResetMissionRouteAndWaypoints, optimizeExistingMission, saveOptimizedWaypoint } from '../../api/commande';
import UploadRouteLetterFile from '../UploadRouteLetterFile/UploadRouteLetterFileExistingMission';
import { useAuthenticatedFetch } from '../../hooks';
import { Trash2, Replace } from 'lucide-react';
import ActionModal from '../ValidationModal/ActionModal';
import SimulateOptimization from './SimulateOptimization';
import { convertMinutesToHHMM } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';


// Styles pour la modal
const modalStyles = {
  content: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: '0',
    borderRadius: '10px',
    border: 'none',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    fontFamily: 'Montserrat, sans-serif',
    maxHeight: '80vh',
    overflow: 'hidden',
    outline: 'none',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1001,
  },
};

// Styles pour le header de la modal
const headerStyles = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  background: '#5BBCEA',
  padding: '20px',
  position: 'relative',
  borderTopLeftRadius: '10px',
  borderTopRightRadius: '10px',
};

// Styles pour le bouton de fermeture
const closeButtonStyles = {
  position: 'absolute',
  right: '15px',
  backgroundColor: 'transparent',
  padding: '0',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: '35px',
  height: '35px',
  cursor: 'pointer',
  fontSize: '22px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 'bold',
  transition: 'background-color 0.3s, color 0.3s',
};

// Styles pour le contenu de la modal
const contentStyles = {
  padding: '20px',
  background: '#F7F8F8',
  overflowY: 'auto',
  maxHeight: 'calc(80vh - 60px)',
};

// Styles pour chaque section
const sectionStyles = {
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '2.5rem',
  width: '100%',
};

// Styles pour l'en-tête de section
const sectionHeaderStyles = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};

// Styles pour chaque item de lettre de route
const letterItemStyles = {
  marginBottom: '2rem',
  padding: '1.5rem',
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
};

// Styles pour l'indicateur de chargement
const loadingStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'white',
  marginTop: '2rem',
  marginBottom: '2rem',
  height: '200px',
};

// Styles pour le loader
const loaderStyles = {
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #5BBCEA',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  animation: 'spin 1s linear infinite',
};

// Styles pour le message vide
const emptyStyles = {
  background: '#fff',
  padding: '15px',
  borderRadius: '8px',
  marginTop: '15px',
  marginBottom: '15px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s',
  textAlign: 'center',
  color: '#718096',
};
const optimizationStyle = {
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    borderRadius: '10px',
    border: 'none',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    fontFamily: 'Montserrat, sans-serif',
    maxHeight: '80vh',
    overflow: 'hidden',
    width: '80%'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #5BBCEA 0%, #2196F3 100%)',
    padding: '24px 32px',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '16px'
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr', // Deux colonnes égales
    gap: '20px', // Espace entre les cartes
  },
  card: {
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '20px',
    background: '#FFFFFF',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    height: '100%', // Pour égaliser la hauteur
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  cardHeader: {
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    color: '#111827',
    fontSize: '18px',
    fontWeight: '600',
    textAlign: 'center'
  },
  metricsContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: '24px'
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px'
  },
  metricLabel: {
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: '500'
  },
  metricValue: {
    color: '#1F2937',
    fontSize: '16px',
    fontWeight: '600'
  },
  chooseButton: {
    background: '#51C7F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.2s',
    ':hover': {
      background: '#2563EB'
    }
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #E5E7EB'
  },
  warningText: {
    color: '#6B7280',
    fontSize: '14px',
    flex: 1,
    marginRight: '20px'
  },
  cancelButton: {
    background: 'red',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      background: '#F3F4F6'
    }
  }
};

const RouteLetterModal = ({
  isOpen,
  onClose,
  missionId,
  index,
  hideAddFile = false,
  hideRemoveFile = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [optimizationLoading, setOptimizationLoading] = useState(false);
  const token = useSelector((state) => state.auth.token);
  const [fileUrls, setFileUrls] = useState({});
  const [routeLetters, setRouteLetters] = useState([]);
  const [optimization, setOptimization] = useState('distance');
  const [showOptimizationModal, setShowOptimizationModal] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [showOptimizationConfirmation, setShowOptimizationConfirmation] = useState(false);
  const { t } = useTranslation()
  const [optimizationChoices, setOptimizationChoices] = useState({
    distance: null,
    duration: null
  });
  const { control, watch, setValue } = useForm({
    defaultValues: {
      lettres_route: null,
      originalFile: null,
      simulation: null
    }
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    letterId: null,
    letterName: ''
  });
  const [processingDelete, setProcessingDelete] = useState(false);
  const [replaceConfirmation, setReplaceConfirmation] = useState({
    isOpen: false,
    letterId: null,
    letterName: ''
  });
  const [processingReplace, setProcessingReplace] = useState(false);
  const [uploadKey, setUploadKey] = useState(Date.now());
  const {
    fetchResource,
    error: fetchError,
    loading: fetchLoading,
  } = useAuthenticatedFetch();

  const handleOptimize = async () => {
    try {
      setOptimizationLoading(true);
      const response = await optimizeExistingMission({
        missionId,
      }, token);
      if (response.status) {
        setOptimizationChoices({
          distance: response.optimizations.distance,
          duration: response.optimizations.duration
        });
        onClose()
        setShowOptimizationModal(true);
      }


    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || t("transfer_request_err"), {
        style: { fontFamily: 'Montserrat' },
      });
    } finally {
      setOptimizationLoading(false);
    }
  };

  const handleConfirmOptimization = async () => {
    try {
      setLoading(true);
      await saveOptimizedWaypoint({
        missionId,
        simulationData: simulation
      }, token)
      toast.success(t("optimization_confirmed"), {
        style: { fontFamily: 'Montserrat' },
      });
      await fetchRouteLetter(missionId);
      setShowOptimizationModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || t("error_during_confirmation"), {
        style: { fontFamily: 'Montserrat' },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setProcessingDelete(true);
    try {
      await softResetMissionRouteAndWaypoints(missionId, token);
      toast.success(t("waybills_successfully_deleted"), {
        style: { fontFamily: 'Montserrat' }
      });
      await fetchRouteLetter(missionId);
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || t("error_during_deletion"), {
        style: { fontFamily: 'Montserrat' }
      });
    } finally {
      setProcessingDelete(false);
      setDeleteConfirmation({ isOpen: false, letterId: null, letterName: '' });
    }
  };

  const loadFileUrls = async () => {
    const urls = {};
    for (const letter of routeLetters) {
      try {
        const url = await fetchResource(letter.path, token);
        urls[letter._id] = url;
      } catch (error) {
        console.error('Error loading file URL:', error);
      }
    }
    setFileUrls(urls);
  };

  const applyOptimization = (type) => {
    const newData = optimizationChoices[type];
    setSimulation(newData);
    setShowOptimizationModal(true);

  };
  useEffect(() => {
    if (routeLetters.length > 0) {
      loadFileUrls();
    }
  }, [routeLetters]);

  const fetchRouteLetter = async (missionId) => {
    setLoading(true);
    try {
      const res = await getRouteLetterByMissionId(missionId, token);
      setRouteLetters(res.routeLetters || []);
    } catch (error) {
      toast.error(t("error_loading_waybills"), {
        style: { fontFamily: 'Montserrat' },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (missionId && isOpen) {
      fetchRouteLetter(missionId);
    }
  }, [missionId, isOpen]);

  const handleReplaceConfirm = async () => {
    setProcessingReplace(true);
    try {
      await softResetMissionRouteAndWaypoints(missionId, token);
      toast.success(t("old_waybills_deleted"), {
        style: { fontFamily: 'Montserrat' }
      });
      setUploadKey(Date.now());
      setRouteLetters([]);
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || t("error_during_replacement"), {
        style: { fontFamily: 'Montserrat' }
      });
    } finally {
      setProcessingReplace(false);
      setReplaceConfirmation({ isOpen: false, letterId: null, letterName: '' });
    }
  };
  const [isSmallScreen, setSmallScreen] = useState(() => window.innerWidth <= 768);


  useEffect(() => {
    const checkIfMobile = () => {
      setSmallScreen(window.innerWidth <= 768);
    };
    window.addEventListener('resize', checkIfMobile);

    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const renderRouteLetters = () => {
    if (routeLetters.length === 0) {
      return (
        <div style={emptyStyles}>
          {t("no_waybills_available")}
        </div>
      );
    }


    return routeLetters.map((letter) => (
      <div key={letter._id} style={letterItemStyles}>
        <div style={{
          display: 'flex',
          flexDirection: isSmallScreen ? "column" : "row",
          justifyContent: 'space-between',
          alignItems: 'center',

        }}>
          <div style={{
            flex: 1, overflow: 'hidden',
            overflowWrap: 'anywhere',
            whiteSpace: 'normal',
          }}>
            <a
              href={fileUrls[letter._id]}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#087ea4',
                textDecoration: 'none',

                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {letter.name}
            </a>
            <div style={{
              fontSize: '0.875rem',
              color: '#718096',
              marginTop: '0.5rem'
            }}>
              {t("added_on")} {new Date(letter.createdAt).toLocaleDateString()}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem',marginTop: isSmallScreen? '1rem' : 0 }}>
            <button
              onClick={() => setDeleteConfirmation({
                isOpen: true,
                letterId: letter._id,
                letterName: letter.name
              })}
              style={{
                padding: '0.5rem',
                background: '#fff0f0',
                border: '1px solid #ffcccc',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Trash2 size={18} color="#ff4444" />
            </button>

            <button
              onClick={() => setReplaceConfirmation({
                isOpen: true,
                letterId: letter._id,
                letterName: letter.name
              })}
              style={{
                padding: '0.5rem',
                background: '#fff5e6',
                border: '1px solid #ffdd99',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Replace size={18} color="#ff9900" />
            </button>
          </div>
        </div>
      </div>
    ));
  };


  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={{
          content: { width: isSmallScreen ? "90%" : "70%", ...modalStyles.content },
          overlay: modalStyles.overlay
        }}
        ariaHideApp={false}
      >
        <div style={headerStyles}>
          <h3 style={{ color: 'white', margin: 0, flex: 1, textAlign: 'center' }}>{t("waybills")}</h3>
          <button onClick={onClose} style={closeButtonStyles}>
            X
          </button>
        </div>

        {loading ? (
          <div style={loadingStyles}>
            <div style={loaderStyles}></div>
          </div>
        ) : (
          <div style={contentStyles}>
            <div style={sectionStyles}>
              <div style={sectionHeaderStyles}>
                <span style={{ color: 'black', fontSize: 14, fontWeight: 600 }}>
                  {t("existing_documents")}
                </span>
                {!hideAddFile && routeLetters.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem', }}>
                    <button
                      onClick={handleOptimize}
                      style={{
                        padding: '8px 16px',
                        background: '#51C7F2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: optimizationLoading ? 'not-allowed' : 'pointer',
                        opacity: optimizationLoading ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // gap: '6px',
                        width: isSmallScreen ? '8rem' : ''

                      }}
                    >
                      {t("optimize")}
                    </button>
                  </div>
                )}
              </div>

              {renderRouteLetters()}

              <UploadRouteLetterFile
                key={uploadKey}
                onClose={onClose}
                initialFiles={watch(`lettres_route`)}
                name={`lettres_route`}
                missionId={missionId}
                onChange={(file) => {
                  setValue(`lettres_route`, file);
                }}
                simulation={watch(`simulation`)}
                onSimulationComplete={(simulationData) => {
                  setValue(`simulation`, simulationData);
                }}
              />
            </div>
          </div>
        )}
      </Modal>
      <ActionModal
        isOpen={showOptimizationConfirmation}
        onRequestClose={() => setShowOptimizationConfirmation(false)}
        title={t("optimization_confirmation")}
        message={t("optimization_confirmation_msg")}
        onConfirm={handleOptimize}
        onCancel={() => setShowOptimizationConfirmation(false)}
        isLoading={optimizationLoading}
        confirmText={t("confirm_optimization")}
        cancelText={t("cancel")}
      />
      <ActionModal
        isOpen={deleteConfirmation.isOpen}
        onRequestClose={() => setDeleteConfirmation({ isOpen: false, letterId: null, letterName: '' })}
        title={t("delete_route_letters")}
        message={`${t("delete_route_letter_confirmation_msg1")} "${deleteConfirmation.letterName}" ? ${t("delete_route_letter_confirmation_msg2")}`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmation({ isOpen: false, letterId: null, letterName: '' })}
        isLoading={processingDelete}
      />

      <ActionModal
        isOpen={replaceConfirmation.isOpen}
        onRequestClose={() => setReplaceConfirmation({ isOpen: false, letterId: null, letterName: '' })}
        title={t("waybills_replacement")}
        message={`${t("waybills_replacement_msg1")} (${routeLetters.length} ${t("files")}) 
        ${t("waybills_replacement_msg2")}`}
        onConfirm={handleReplaceConfirm}
        onCancel={() => setReplaceConfirmation({ isOpen: false, letterId: null, letterName: '' })}
        isLoading={processingReplace}
        confirmText={t("confirm_replacement")}
        cancelText={t("cancel")}
      />

      <Modal
        isOpen={showOptimizationModal}
        style={{ content: optimizationStyle.modalContent }}
        ariaHideApp={false}
        onRequestClose={() => setShowOptimizationModal(false)}
      >

        <div style={optimizationStyle.header}>
          <h3 style={{ margin: 0, color: 'white', textAlign: 'center' }}>{t("choose_optimization_type")}</h3>
        </div>
        <div style={optimizationStyle.container}>
          {optimizationChoices?.distance && optimizationChoices?.duration && (
            <div style={optimizationStyle.cardsRow}>
              {/* Carte Temps */}
              <div style={optimizationStyle.card}>
                <div style={optimizationStyle.cardHeader}>
                  <h4 style={optimizationStyle.title}>{t("time_optimization")}</h4>
                </div>
                <div style={optimizationStyle.metricsContainer}>
                  <div style={optimizationStyle.metricItem}>
                    <span style={optimizationStyle.metricLabel}>{t("duration")}</span>
                    <span style={optimizationStyle.metricValue}>
                      {convertMinutesToHHMM(optimizationChoices?.duration?.totalDuration)}

                    </span>
                  </div>
                  <div style={optimizationStyle.metricItem}>
                    <span style={optimizationStyle.metricLabel}>Distance</span>
                    <span style={optimizationStyle.metricValue}>
                      {optimizationChoices?.duration?.totalDistance?.toFixed(0)} km

                    </span>
                  </div>
                </div>
                <button
                  style={optimizationStyle.chooseButton}
                  onClick={() => applyOptimization('duration')}
                >
                  {t("choose")}
                </button>
              </div>

              {/* Carte Distance */}
              <div style={optimizationStyle.card}>
                <div style={optimizationStyle.cardHeader}>
                  <h4 style={optimizationStyle.title}>{t("distance_optimization")}</h4>
                </div>
                <div style={optimizationStyle.metricsContainer}>
                  <div style={optimizationStyle.metricItem}>
                    <span style={optimizationStyle.metricLabel}>{t("duration")}</span>
                    <span style={optimizationStyle.metricValue}>
                      {convertMinutesToHHMM(optimizationChoices?.distance?.totalDuration)}
                    </span>
                  </div>
                  <div style={optimizationStyle.metricItem}>
                    <span style={optimizationStyle.metricLabel}>Distance</span>
                    <span style={optimizationStyle.metricValue}>
                      {optimizationChoices?.distance?.totalDistance?.toFixed(0)} km

                    </span>
                  </div>
                </div>
                <button
                  style={optimizationStyle.chooseButton}
                  onClick={() => applyOptimization('distance')}
                >
                  {t("choose")}
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={optimizationStyle.footer}>
            <span style={optimizationStyle.warningText}>
              {t("optimisation_msg")}
            </span>
            <button
              style={optimizationStyle.cancelButton}
              onClick={() => setShowOptimizationModal(false)}
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      </Modal>

      <SimulateOptimization
        isOpen={showOptimizationModal}
        onClose={() => setShowOptimizationModal(false)}
        onConfirm={handleConfirmOptimization}
        simulation={simulation}
        optimization={optimization}
        isLoading={loading}
      />
    </>
  );
};

export default RouteLetterModal;